import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Sparkles, CheckCircle2 } from "lucide-react";

export default function InquiryModal({ open, onOpenChange, product, variant }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  function close(v) {
    if (!v) { setSent(false); setName(""); setEmail(""); setPhone(""); setMessage(""); }
    onOpenChange(v);
  }

  async function submit(e) {
    e.preventDefault();
    if (!message.trim()) { toast.error("Please add a short message so we know how to help."); return; }
    setSubmitting(true);
    try {
      await api.post("/inquiries", {
        product_id: product.id,
        customer_name: name,
        customer_email: email,
        customer_phone: phone || null,
        message,
        variant_id: variant?.id || null,
        variant_name: variant?.name || null,
      });
      setSent(true);
      toast.success("Your request has been sent.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send your request. Try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid="inquiry-modal" className="max-w-lg bg-[#FDFBF7] border-[#EAE4D3] rounded-3xl">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[var(--accent)]" />
            {sent ? "Request received" : "Request a private viewing"}
          </DialogTitle>
          <DialogDescription className="text-[#5C5547]">
            {sent
              ? "We'll reply within 24 hours with availability and any next steps."
              : `Tell us a little about what you're looking for in the ${product?.title || "piece"}. We'll respond personally.`}
          </DialogDescription>
        </DialogHeader>

        {!sent && (
          <form onSubmit={submit} className="space-y-3 mt-2" data-testid="inquiry-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" required
                data-testid="inquiry-name"
                className="sm:col-span-2 px-4 py-3 rounded-xl bg-[#FDFBF7] border border-[#EAE4D3] focus:outline-none focus:ring-2 focus:ring-[#D4AF37] text-sm" />
              <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="Email" required
                data-testid="inquiry-email"
                className="px-4 py-3 rounded-xl bg-[#FDFBF7] border border-[#EAE4D3] focus:outline-none focus:ring-2 focus:ring-[#D4AF37] text-sm" />
              <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone (optional)"
                data-testid="inquiry-phone"
                className="px-4 py-3 rounded-xl bg-[#FDFBF7] border border-[#EAE4D3] focus:outline-none focus:ring-2 focus:ring-[#D4AF37] text-sm" />
            </div>
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={5} required
              placeholder={`E.g. "I'd love to see the ${product?.title || "piece"}${variant?.name ? ` in ${variant.name}` : ""}. Could we set up a private viewing?"`}
              data-testid="inquiry-message"
              className="w-full px-4 py-3 rounded-xl bg-[#FDFBF7] border border-[#EAE4D3] focus:outline-none focus:ring-2 focus:ring-[#D4AF37] text-sm" />
            <button type="submit" disabled={submitting} data-testid="inquiry-submit"
              className="w-full py-3 rounded-full bg-[#2C2924] text-[#FDFBF7] hover:bg-[#1A1815] text-sm disabled:opacity-60">
              {submitting ? "Sending…" : "Send request"}
            </button>
            <p className="text-[10px] text-[#5C5547] text-center">No charge until you approve. Replies usually within 24 hours.</p>
          </form>
        )}

        {sent && (
          <div className="text-center py-6" data-testid="inquiry-success">
            <div className="w-14 h-14 mx-auto rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
              <CheckCircle2 className="w-7 h-7 text-[#D4AF37]" />
            </div>
            <div className="mt-4 font-serif text-2xl">Thank you, {name.split(" ")[0]}.</div>
            <p className="mt-2 text-sm text-[#5C5547]">We'll be in touch at <strong>{email}</strong>.</p>
            <button onClick={() => close(false)} data-testid="inquiry-success-close"
              className="mt-6 px-6 py-2.5 rounded-full bg-[#2C2924] text-[#FDFBF7] text-sm">Close</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
